import {inject, isWritableSignal, signal, WritableSignal} from "@angular/core";

export class NavigationParams {

    constructor(private params: Record<string, any>) {
    }

    public get(param: string) {
        return this.params?.[param];
    }
}

/**
 * Creates a writable signal initialized from a navigation parameter.
 * Similar to Angular's input() function, but for navigation parameters.
 *
 * If the parameter value is already a WritableSignal, it is returned as-is,
 * allowing for mutable parameter passing between pages.
 *
 * @param paramName The name of the navigation parameter to retrieve
 * @param defaultValue Optional default value if parameter is undefined
 * @returns A WritableSignal containing the parameter value
 *
 * @example
 * export class MyComponent {
 *     private readonly myParam = navParam<string>("myParam");
 *     private readonly paramWithDefault = navParam<string>("param", "defaultValue");
 *     private readonly requiredParam = navParam.required<string>("requiredParam");
 *     private readonly hasParam = navParam.exists("someParam");
 *     // Use as: this.myParam() or this.myParam.set(newValue)
 * }
 */
export function navParam<T>(paramName: string, defaultValue?: T): WritableSignal<T | undefined> {
    const value: T | undefined = inject(NavigationParams).get(paramName) ?? defaultValue;
    return isWritableSignal(value) ? value as WritableSignal<T | undefined> : signal<T | undefined>(value);
}

navParam.required = function <T>(paramName: string): WritableSignal<T> {
    const value: T = inject(NavigationParams).get(paramName);
    if (value === undefined) {
        throw new Error(`Required navigation parameter '${paramName}' is missing`);
    }
    return isWritableSignal(value) ? value as WritableSignal<T> : signal<T>(value);
};

navParam.exists = function (paramName: string): WritableSignal<boolean> {
    return signal<boolean>(inject(NavigationParams).get(paramName) !== undefined);
};
